import { useEffect, useState, type ReactNode } from 'react';
import type { BoardroomCompare, BoardroomCompareColumn } from '../../../types';
import DownloadCsv from './DownloadCsv';

export const MAX_COMPARE = 4;

const STORE = 'jcseo.boardroom.compare.';

/** The companies last compared in this room, in this browser. */
export function storedPicks(boardroomId: number): number[] {
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORE + boardroomId) || '[]');
    return Array.isArray(raw) ? raw.filter((n): n is number => Number.isInteger(n) && n > 0).slice(0, MAX_COMPARE) : [];
  } catch {
    return [];
  }
}

/** The picks a `?compare=12,34` address names, in order. Pure. */
export function parseCompare(param: string | null): number[] {
  const ids = (param ?? '')
    .split(',')
    .map(Number)
    .filter(n => Number.isInteger(n) && n > 0);
  return Array.from(new Set(ids)).slice(0, MAX_COMPARE);
}

/** `ids` with this company first, keeping at most MAX_COMPARE. Pure. */
export function withFirst(ids: number[], id: number): number[] {
  return [id, ...ids.filter(i => i !== id)].slice(0, MAX_COMPARE);
}

/** One row per company, one column per row of the table. Pure. */
export function compareCsv(cols: BoardroomCompareColumn[]): string {
  const cell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const header = ['Company', ...CSV_ROWS.map(r => r.label)];
  const lines = cols.map(c => [c.title, ...CSV_ROWS.map(r => r.text(c))]);
  return [header, ...lines].map(row => row.map(cell).join(',')).join('\n');
}

function remember(boardroomId: number, ids: number[]) {
  try {
    window.localStorage.setItem(STORE + boardroomId, JSON.stringify(ids));
  } catch {
    /* private mode: per visit */
  }
}

const list = (xs: string[]) => (xs.length ? xs.join(', ') : null);

/** The table's rows: a name, and what each company's cell says. */
const ROWS: { label: string; cell: (c: BoardroomCompareColumn) => ReactNode }[] = [
  {
    label: 'Sets it apart',
    cell: c =>
      c.apart.length ? <span className="text-emerald-700 dark:text-emerald-300">{c.apart.join('. ')}</span> : null,
  },
  { label: 'Product', cell: c => list(c.products) },
  { label: 'Price', cell: c => c.price },
  { label: 'Who it is for', cell: c => list(c.audiences) },
  { label: 'Pitched to', cell: c => list(c.roles) },
  { label: 'Leads with', cell: c => list(c.angles) },
  { label: 'Main promise', cell: c => c.promise },
  { label: 'Proof shown', cell: c => list(c.proof) },
  { label: 'Features recorded', cell: c => (c.features ? String(c.features) : null) },
  {
    label: 'Complaints',
    cell: c =>
      c.complaint_count ? (
        <>
          {c.complaints.join('; ')}
          {c.complaint_count > c.complaints.length && (
            <span className="text-gray-400"> · and {c.complaint_count - c.complaints.length} more</span>
          )}
        </>
      ) : null,
  },
];

const joined = (xs: string[]) => xs.join('; ');

/** The same rows as text, for the download. */
const CSV_ROWS: { label: string; text: (c: BoardroomCompareColumn) => string }[] = [
  { label: 'Sets it apart', text: c => joined(c.apart) },
  { label: 'Product', text: c => joined(c.products) },
  { label: 'Price', text: c => c.price ?? '' },
  { label: 'Who it is for', text: c => joined(c.audiences) },
  { label: 'Pitched to', text: c => joined(c.roles) },
  { label: 'Leads with', text: c => joined(c.angles) },
  { label: 'Main promise', text: c => c.promise ?? '' },
  { label: 'Proof shown', text: c => joined(c.proof) },
  { label: 'Features recorded', text: c => (c.features ? String(c.features) : '') },
  { label: 'Complaints', text: c => joined(c.complaints) },
];

/** A cell with nothing in it says why, rather than going blank. */
function Empty({ c, label }: { c: BoardroomCompareColumn; label: string }) {
  const unread =
    !c.read && ['Who it is for', 'Pitched to', 'Leads with', 'Main promise', 'Proof shown'].includes(label);
  return <span className="text-gray-400 dark:text-gray-500">{unread ? 'Pages not read yet' : 'None yet'}</span>;
}

/**
 * COMPARE: two to four companies side by side.
 *
 * Seeing how three rivals differ meant reading across four tables - price in
 * one, features in another, the pitch in a third, complaints in a fourth. Here
 * each company is a column, worked out on the server off the same reports
 * (`jcseo/boardroom/compare.py`), with what sets each apart from the others
 * picked, and what they all share.
 */
export default function ComparePanel({
  boardroomId,
  load,
  onOpenItem,
  initial,
  onPicks,
  lensKey = '',
}: {
  boardroomId: number;
  /** The picks' columns, and the companies in the lens to add from. */
  load: (ids: number[]) => Promise<BoardroomCompare>;
  /** Changes when the lens does, so the companies to add are fetched again. */
  lensKey?: string;
  onOpenItem: (id: number) => void;
  /** The picks to open with (the address's); the last ones used otherwise. */
  initial?: number[];
  /** The picks changed, so the address can follow and be shared. */
  onPicks?: (ids: number[]) => void;
}) {
  const [ids, setIds] = useState<number[]>(() => (initial?.length ? initial : storedPicks(boardroomId)));
  const [data, setData] = useState<BoardroomCompare | null>(null);
  const [adding, setAdding] = useState('');
  useEffect(() => {
    let alive = true;
    load(ids)
      .then(d => alive && setData(d))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // `load` is a fresh closure each render; the picks and the lens say when
    // to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(','), lensKey]);
  const pick = (next: number[]) => {
    setIds(next);
    remember(boardroomId, next);
    onPicks?.(next);
  };
  const cols = data?.companies ?? [];
  const choices = (data?.choices ?? []).filter(c => !ids.includes(c.id));

  return (
    <div className="flex flex-col gap-4" data-testid="compare-panel">
      <div className="flex flex-wrap items-center gap-2">
        {cols.map(c => (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-sm text-indigo-900 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-100"
          >
            {c.title}
            <button
              type="button"
              onClick={() => pick(ids.filter(i => i !== c.id))}
              aria-label={`Take ${c.title} out`}
              className="text-indigo-500 hover:text-indigo-800 dark:hover:text-indigo-200"
            >
              ×
            </button>
          </span>
        ))}
        {ids.length < MAX_COMPARE && (
          <select
            value={adding}
            onChange={e => {
              const id = Number(e.target.value);
              setAdding('');
              if (id) pick([...ids, id]);
            }}
            aria-label="Add a company"
            className="rounded-full border border-dashed border-gray-400 bg-transparent px-2.5 py-1 text-sm text-gray-600 dark:border-gray-500 dark:text-gray-300"
            data-testid="compare-add"
          >
            <option value="">+ Add a company</option>
            {choices.map(c => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        )}
      </div>

      {!data ? (
        <p className="text-xs text-gray-400">Reading the room…</p>
      ) : cols.length < 2 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Pick {cols.length ? 'one more company' : 'two or more companies'} to see them side by side.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full min-w-[40rem] border-collapse text-sm" data-testid="compare-table">
              <thead>
                <tr className="bg-gray-50 text-left dark:bg-gray-800">
                  <th className="w-36 px-3 py-2" />
                  {cols.map(c => (
                    <th key={c.id} className="px-3 py-2 font-semibold text-gray-900 dark:text-gray-100">
                      <button type="button" onClick={() => onOpenItem(c.id)} className="hover:underline">
                        {c.title}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(row => (
                  <tr key={row.label} className="border-t border-gray-200 align-top dark:border-gray-700">
                    <th
                      scope="row"
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      {row.label}
                    </th>
                    {cols.map(c => (
                      <td key={c.id} className="px-3 py-2 text-gray-800 dark:text-gray-100">
                        {row.cell(c) ?? <Empty c={c} label={row.label} />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <DownloadCsv csv={() => compareCsv(cols)} filename="compare.csv" label="CSV" />
          </div>
          {data.shared.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="compare-shared">
              {data.shared.join('. ')}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
